import { isAfter, isSameDay } from 'date-fns';
import { useTaskTodayControls, useUpdateTask } from 'features/tasks';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar16,
  Info12,
  MoreHorizontal,
  Plus,
  Repeat16,
} from 'shared/assets/icons';
import { DatepickerWithOptions } from 'shared/components/ui/datepicker-with-options';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { TimeRangeContextMenu } from 'shared/components/ui/time-range-context-menu';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { createPlannedOccurrenceForDate } from 'shared/utils/schedules';
import { getScheduleByDate } from 'shared/utils/schedules/get-schedule-by-date';

import { useData } from '../../use-data';
import { DayEntry } from '../upcoming/types';
import { ActionMenu } from './action-menu';
import * as Styled from './upcoming-slot.style';
import { useSlotData } from './use-slot-data';

enum MenuOptions {
  Date = 'date',
  Time = 'time',
  Default = 'default',
}

export type UpcomingSlotProps = {
  task: Task;
  date: Timestamp;
  entry?: DayEntry;
  isOverdue?: boolean;
};

export const UpcomingSlot: React.FC<UpcomingSlotProps> = ({
  task,
  date,
  entry,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn, timeFormat, dateFormat } = useData();

  const infoIconRef = useRef<HTMLSpanElement>(null);
  const actionMenuButtonRef = useRef<HTMLButtonElement>(null);
  const frequencyActionMenuButtonRef = useRef<HTMLButtonElement>(null);
  const timeButtonRef = useRef<HTMLButtonElement>(null);

  const [menuOpen, setMenuOpen] = useState<MenuOptions>();
  const openTimeMenu = () => setMenuOpen(MenuOptions.Time);
  const openDefaultMenu = () => setMenuOpen(MenuOptions.Default);
  const openDateMenu = () => setMenuOpen(MenuOptions.Date);
  const closeMenu = () => setMenuOpen(undefined);

  const { onComplete } = useTaskTodayControls();
  const { submit: updateTask } = useUpdateTask();

  const {
    isToday,
    label,
    checked,
    completionPercentage,
    isFuture,
    overdue,
    target,
    timeLabel,
  } = useSlotData({ task, date, entry });

  const _onComplete = useCallback(() => {
    if (
      task.schedules?.length &&
      !isSameDay(date, today) &&
      isAfter(date, today)
    ) {
      return;
    }

    const daySchedule = getScheduleByDate(task.schedules, { date, today });
    onComplete(task, ScheduleEntryState.Complete, {
      referenceDate: date,
      toggleDayEntry: !!daySchedule?.frequency,
      keepDetailOpen: true,
    });
  }, [task, date, today, onComplete]);

  const _onAdd = () => {
    if (!task.schedules?.length || entry) {
      return;
    }

    const scheduleForDate = getScheduleByDate(task.schedules, { date, today });
    const schedules = createPlannedOccurrenceForDate(task.schedules, date, {
      timeslot: scheduleForDate?.timeslot,
      today,
      weekStartsOn,
    });

    updateTask({ id: task.id, schedules });
  };

  const onChangeTimeslot = (timeslot: Timeslot | null) => {
    if (!task.schedules?.length || !entry) {
      return;
    }

    const schedules = createPlannedOccurrenceForDate(
      task.schedules,
      entry.date,
      {
        timeslot,
        today,
        weekStartsOn,
        id: entry.id,
        originalDate: entry.date,
      },
    );

    updateTask({ id: task.id, schedules });
  };

  const onChangeDate = (newDate?: Timestamp) => {
    if (!task.schedules?.length || !newDate) {
      return;
    }

    const schedules = createPlannedOccurrenceForDate(task.schedules, newDate, {
      today,
      weekStartsOn,
      originalDate: date,
    });

    updateTask({ id: task.id, schedules });
    closeMenu();
  };

  return (
    <>
      {(!entry?.isGeneratedFrequencyEntry || isToday) && (
        <Styled.Container as={entry ? 'div' : undefined} onClick={_onAdd}>
          {entry ? (
            !isFuture || isToday ? (
              <TaskDetailCheckbox
                checked={checked}
                name={task.name}
                type={task.type}
                completionPercentage={completionPercentage}
                target={target}
                priority={task?.priority}
                onChange={_onComplete}
                isFrozen={!!task?.frozenAt}
                icon={entry.isGeneratedFrequencyEntry ? Repeat16 : Calendar16}
              />
            ) : (
              <Styled.StartIconContainer>
                <Icon
                  icon={entry.isGeneratedFrequencyEntry ? Repeat16 : Calendar16}
                />
              </Styled.StartIconContainer>
            )
          ) : (
            <Styled.StartIconContainer>
              <Icon icon={Plus} />
            </Styled.StartIconContainer>
          )}
          <Styled.LabelContainer
            $overdue={overdue}
            $hasEntry={!!entry}
            $isToday={isToday}
          >
            <Styled.LabelWrapper>
              <Styled.LabelText>
                {label}
                {entry?.hidden ? ' (hs)' : ''}
              </Styled.LabelText>
            </Styled.LabelWrapper>
          </Styled.LabelContainer>

          {entry && (
            <Styled.EndContainer>
              <Styled.TimeButton
                ref={timeButtonRef}
                type="button"
                onClick={openTimeMenu}
              >
                {timeLabel}
              </Styled.TimeButton>
              <Tooltip label={timeLabel} referenceElement={timeButtonRef} />
              <IconButton
                icon={MoreHorizontal}
                onClick={openDefaultMenu}
                color={Colors.Default}
                size={Sizes.Small}
                ref={actionMenuButtonRef}
              />
              {menuOpen === MenuOptions.Date && (
                <DatepickerWithOptions
                  value={date}
                  onChange={onChangeDate}
                  referenceElement={actionMenuButtonRef}
                  onOutside={closeMenu}
                  dateFormat={dateFormat}
                  weekStartsOn={weekStartsOn}
                  canRemoveValue={false}
                />
              )}
              {menuOpen === MenuOptions.Default && (
                <ActionMenu
                  task={task}
                  date={date}
                  timeLabel={timeLabel}
                  onDate={openDateMenu}
                  onTime={openTimeMenu}
                  referenceElement={actionMenuButtonRef}
                  onOutside={closeMenu}
                  entryBased={entry.isUnplannedEntry}
                  isGeneratedFrequencyEntry={entry.isGeneratedFrequencyEntry}
                />
              )}

              {menuOpen === MenuOptions.Time && (
                <TimeRangeContextMenu
                  referenceElement={timeButtonRef}
                  onChange={onChangeTimeslot}
                  onClose={closeMenu}
                  timeFormat={timeFormat}
                  value={entry.timeslot}
                />
              )}
            </Styled.EndContainer>
          )}
        </Styled.Container>
      )}

      {entry?.isGeneratedFrequencyEntry && (
        <Styled.Container as="div">
          <Styled.StartIconContainer>
            <Icon icon={Repeat16} />
          </Styled.StartIconContainer>
          <Styled.LabelContainer $hasEntry>
            <Styled.LabelText>
              {t('habit.schedule.frequency.until-completed')}
            </Styled.LabelText>
            <Styled.InfoIconContainer ref={infoIconRef}>
              <Icon icon={Info12} width="1.2rem" height="1.2rem" />
            </Styled.InfoIconContainer>
            <Tooltip
              label={t('habit.schedule.frequency.until-completed.tooltip')}
              referenceElement={infoIconRef}
            />
          </Styled.LabelContainer>
          <Styled.EndContainer>
            <IconButton
              icon={MoreHorizontal}
              onClick={openDefaultMenu}
              color={Colors.Default}
              size={Sizes.Small}
              ref={frequencyActionMenuButtonRef}
            />
            {menuOpen === MenuOptions.Default && (
              <ActionMenu
                task={task}
                date={date}
                timeLabel={timeLabel}
                onDate={openDateMenu}
                onTime={openTimeMenu}
                referenceElement={frequencyActionMenuButtonRef}
                onOutside={closeMenu}
                entryBased={entry.isUnplannedEntry}
                isGeneratedFrequencyEntry={entry.isGeneratedFrequencyEntry}
                isFrequencyRow={true}
              />
            )}
          </Styled.EndContainer>
        </Styled.Container>
      )}
    </>
  );
};
